import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  PublicRestService,
  PublicSablonyFiltrDto,
  PublicSablonyVysledekDto,
  RestNaseptatCiselnikService,
} from '@generated/be';
import { resetPageAndSortStartList } from '@reza-ais-fe/reza-ais-fe-utils';
import { ObservableState, Logger, LoggerService, DEFAULT_PAGE_LIMIT, publicApiArgs } from '@shared/utils';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, map, mergeMap, Observable, tap } from 'rxjs';

type KatalogSkupinSablonState = {
  filter: PublicSablonyFiltrDto;
  result: PublicSablonyVysledekDto;
  reloadTrigger: number;
};
const emptyResult = { total: 0, content: [] } as PublicSablonyVysledekDto;

type ViewModel = KatalogSkupinSablonState & {
  // zde můžou být deklarovány další dopočítané atributy
};

@Injectable()
export class KatalogSkupinSablonStateService extends ObservableState<KatalogSkupinSablonState> {
  public readonly SORTING_TYPE_DEFAULT = [];

  DEFAULT_PAGE_AND_SORT = {
    start: 0,
    count: DEFAULT_PAGE_LIMIT,
    sortListPage: this.SORTING_TYPE_DEFAULT,
  };

  public readonly defaultFilter: PublicSablonyFiltrDto = {
    pageAndSortList: this.DEFAULT_PAGE_AND_SORT,
  };

  private readonly publicRestService = inject(PublicRestService);
  private readonly naseptavacService = inject(RestNaseptatCiselnikService);
  readonly loggerService = inject(LoggerService);
  private logger: Logger = this.loggerService.createLogger('KatalogSkupinSablonStateService');

  private readonly sablony$ = this.onlySelectWhen(['filter', 'reloadTrigger']).pipe(
    distinctUntilChanged((prev, curr) => {
      return isEqual(prev.filter, curr.filter) && prev.reloadTrigger === curr.reloadTrigger;
    }),
    mergeMap(({ filter }) => {
      this.logger.debug('vyhledatSablonyKatalog', filter);
      const observable = this.publicRestService.vyhledatPublicSkupinySablon(
        {
          publicSkupinySablonFiltrDto: filter as PublicSablonyFiltrDto,
        },
        ...publicApiArgs()
      );
      return this.handleErrors(observable, emptyResult);
    })
  );

  private initialState: KatalogSkupinSablonState = {
    filter: this.defaultFilter,
    result: emptyResult,
    reloadTrigger: 0,
  };

  isResultConnected = false;

  constructor(private router: Router) {
    super();
    this.initialize(this.initialState);
  }

  public connectSablony() {
    if (!this.isResultConnected) {
      this.isResultConnected = true;
      this.connect({
        result: this.sablony$,
      });
    }
  }

  public findWithClearFilter(clearFiltrVstup: PublicSablonyFiltrDto = {}) {
    const filter = resetPageAndSortStartList(this.snapshot.filter);
    this.patch({
      filter: { ...filter, ...clearFiltrVstup },
    });
  }

  public findWithNewFilter(newFiltrVstup: any = {}) {
    if (newFiltrVstup?.gestor?.kod) {
      newFiltrVstup.gestor = newFiltrVstup.gestor.kod;
    }
    if (newFiltrVstup?.nazev?.nazev) {
      newFiltrVstup.nazev = newFiltrVstup.nazev.nazev;
    }
    const filter = resetPageAndSortStartList(this.snapshot.filter);
    this.patch({
      filter: { ...filter, ...newFiltrVstup },
    });
  }

  public findWithParcialFilter(filtrVstupPartial: PublicSablonyFiltrDto = {}) {
    const filter = this.snapshot.filter;
    this.patch({
      filter: { ...filter, ...filtrVstupPartial },
    });
  }

  public reloadData() {
    this.patch({
      reloadTrigger: this.snapshot.reloadTrigger + 1,
    });
  }

  public readonly vm$: Observable<ViewModel> = this.state$.pipe(
    tap((state) => this.logger.debug('state', state)),
    map(({ filter, result, reloadTrigger }) => {
      return {
        filter,
        result,
        reloadTrigger,
      };
    })
  );
}
